import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { GenNode } from '../types/carbon/models/GenNode'
import { ExecutiveReportData, PlatinumTableData, QuickUpdateRequest, TocNode, ExtendedGenNode } from '../types/types_old'
import useAccount from './useAccount'
import useRouter from './useRouter'
import { findNode } from '../utils/tree'
import { getNodeID } from '../utils/nodeTypes'

interface CategoryConfig {
  id: string
  label: string
  data: ExecutiveReportData | null
  loading: boolean
  error: string | null
}

interface ExecutiveReportJobConfigDTO {
  id: string
  customer: string
  job: string
  label?: string
  manifestJson: any
  createdAt: string
  updatedAt: string
}

interface ExecutiveReportRunRecord {
  id: string
  version: number
  status: string
  triggerReason: string
  triggeredBy?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
}

interface ExecutiveReportRunProgress {
  totalSections: number
  completedSections: number
  failedSections: number
  totalTables: number
  completedTables: number
  failedTables: number
}

interface ExecutiveReportSectionDTO {
  id: string
  status: string
  categoryKey: string
  pillarKey: string
  sectionKey: string
  prompt?: string
  position: number
}

interface ExecutiveReportTableDTO {
  id: string
  status: string
  tableIdentifier: string
  sequence: number
}

interface ExecutiveReportRunSummaryResponse {
  config: ExecutiveReportJobConfigDTO
  run: ExecutiveReportRunRecord
  output: any
  sections: Array<{
    section: ExecutiveReportSectionDTO
    tables: ExecutiveReportTableDTO[]
  }>
  progress?: ExecutiveReportRunProgress
}

// Report type definitions
export type ReportType = 'brand-funnel' | 'category-reports'

interface BrandFunnelReportData {
  metadata: {
    reportType: string
    title: string
    description: string
    generatedAt: string
    categoriesIncluded: number
    totalSections: number
    compiler: string
    version: string
  }
  categories: Array<{
    category: string
    categoryDisplayName: string
    pillarName: string
    pillarDescription: string
    sections: Array<{
      title: string
      content: string
      tablesUsed: string[]
      individualAnalyses: Record<string, any>
      metadata: any
    }>
    metadata: {
      totalTables: number
      analysisCount: number
      generatedAt: string
    }
  }>
  crossCategoryInsights: {
    title: string
    content: string
    metadata: {
      type: string
      generatedAt: string
    }
  }
}

// Helper function to format category label
const formatCategoryLabel = (categoryId: string): string => {
  return categoryId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Extract categories from executive summary data structure
const extractCategoriesFromData = (data: ExecutiveReportData | null): string[] => {
  if (!data) return []
  
  // Check if data has categories field (new structure)
  if (data.categories && typeof data.categories === 'object') {
    return Object.keys(data.categories)
  }
  
  // Otherwise return empty array (will use default)
  return []
}

// Get categories based on job - these mappings are specific to each job's executive summaries
const getCategoriesForJob = (jobName: string | undefined, initialData: ExecutiveReportData | null = null): CategoryConfig[] => {
  // Try to extract categories from the data first (for dynamic structure)
  const dataCategories = extractCategoriesFromData(initialData)
  if (dataCategories.length > 0) {
    return dataCategories.map(categoryId => ({
      id: categoryId,
      label: formatCategoryLabel(categoryId),
      data: null,
      loading: false,
      error: null
    }))
  }
  
  if (!jobName) {
    // Return minimal default categories if no job
    return [{
      id: 'default',
      label: 'Loading...',
      data: null,
      loading: false,
      error: null
    }]
  }
  
  // Map job names to their specific categories (fallback for legacy structure)
  // These should match what's available in each job's executive summaries
  const jobCategoryMap: Record<string, CategoryConfig[]> = {
    'sky-uk-be-prius': [
      { id: 'allsky', label: 'Sky Only', data: null, loading: false, error: null },
      { id: 'broadband', label: 'Sky Broadband', data: null, loading: false, error: null },
      { id: 'mobile', label: 'Sky Mobile', data: null, loading: false, error: null },
      { id: 'tv', label: 'Sky TV Platform', data: null, loading: false, error: null },
      { id: 'tv_set', label: 'Sky TV Glass', data: null, loading: false, error: null },
      { id: 'sport', label: 'Sky Sports', data: null, loading: false, error: null },
      { id: 'cinema', label: 'Sky Cinema', data: null, loading: false, error: null },
      { id: 'now_sport', label: 'NOW Sports', data: null, loading: false, error: null },
      { id: 'nowcinema', label: 'NOW Cinema', data: null, loading: false, error: null },
      { id: 'nowtv', label: 'NOW TV', data: null, loading: false, error: null }
    ],
    'UK Prius': [
      { id: 'allsky', label: 'Sky Only', data: null, loading: false, error: null },
      { id: 'broadband', label: 'Sky Broadband', data: null, loading: false, error: null },
      { id: 'mobile', label: 'Sky Mobile', data: null, loading: false, error: null },
      { id: 'tv', label: 'Sky TV Platform', data: null, loading: false, error: null },
      { id: 'tv_set', label: 'Sky TV Glass', data: null, loading: false, error: null },
      { id: 'sport', label: 'Sky Sports', data: null, loading: false, error: null },
      { id: 'cinema', label: 'Sky Cinema', data: null, loading: false, error: null },
      { id: 'now_sport', label: 'NOW Sports', data: null, loading: false, error: null },
      { id: 'nowcinema', label: 'NOW Cinema', data: null, loading: false, error: null },
      { id: 'nowtv', label: 'NOW TV', data: null, loading: false, error: null }
    ],
    'eire-sky-brand-equity': [
      { id: 'mobile', label: 'Sky Mobile', data: null, loading: false, error: null },
      { id: 'broadband', label: 'Sky Broadband', data: null, loading: false, error: null },
      { id: 'tv', label: 'Sky TV', data: null, loading: false, error: null },
      { id: 'sport', label: 'Sky Sports', data: null, loading: false, error: null },
      { id: 'cinema', label: 'Sky Cinema', data: null, loading: false, error: null }
    ],
    'Ireland Brand Equity': [
      { id: 'mobile', label: 'Sky Mobile', data: null, loading: false, error: null },
      { id: 'broadband', label: 'Sky Broadband', data: null, loading: false, error: null },
      { id: 'tv', label: 'Sky TV', data: null, loading: false, error: null },
      { id: 'sport', label: 'Sky Sports', data: null, loading: false, error: null },
      { id: 'cinema', label: 'Sky Cinema', data: null, loading: false, error: null }
    ],
    '21066-joiners': [
      { id: 'now_inlife', label: 'NOW Inlife', data: null, loading: false, error: null },
      { id: 'now_joiners', label: 'NOW Joiners', data: null, loading: false, error: null }
    ],
    'NOW Joiners': [
      { id: 'now_inlife', label: 'NOW Inlife', data: null, loading: false, error: null },
      { id: 'now_joiners', label: 'NOW Joiners', data: null, loading: false, error: null }
    ],
    '21066-leavers': [
      { id: 'now_leavers_attitudes', label: 'NOW Leavers Attitudes', data: null, loading: false, error: null },
      { id: 'now_leavers_demographics', label: 'NOW Leavers Demographics', data: null, loading: false, error: null }
    ],
    'NOW Leavers': [
      { id: 'now_leavers_attitudes', label: 'NOW Leavers Attitudes', data: null, loading: false, error: null },
      { id: 'now_leavers_demographics', label: 'NOW Leavers Demographics', data: null, loading: false, error: null }
    ],
    'sky-protect': [
      { id: 'home_insurance_sky_customers', label: 'Home Insurance - Sky Customers', data: null, loading: false, error: null },
      { id: 'home_insurance_prospects', label: 'Home Insurance - Prospects', data: null, loading: false, error: null },
      { id: 'smart_tech_sky_customers', label: 'Smart Tech - Sky Customers', data: null, loading: false, error: null },
      { id: 'smart_tech_prospects', label: 'Smart Tech - Prospects', data: null, loading: false, error: null }
    ],
    'Sky Protect': [
      { id: 'home_insurance_sky_customers', label: 'Home Insurance - Sky Customers', data: null, loading: false, error: null },
      { id: 'home_insurance_prospects', label: 'Home Insurance - Prospects', data: null, loading: false, error: null },
      { id: 'smart_tech_sky_customers', label: 'Smart Tech - Sky Customers', data: null, loading: false, error: null },
      { id: 'smart_tech_prospects', label: 'Smart Tech - Prospects', data: null, loading: false, error: null }
    ],
    '24129-fanstrack-womens-football': [
      { id: 'behaviours', label: 'Behaviours', data: null, loading: false, error: null },
      { id: 'Clubs and Players', label: 'Clubs and Players', data: null, loading: false, error: null },
      { id: 'Attitudes', label: 'Attitudes', data: null, loading: false, error: null },
      { id: 'Experience', label: 'Experience', data: null, loading: false, error: null }
    ]
  }
  
  // Return job-specific categories or default ones
  return jobCategoryMap[jobName] || [
    { id: 'default', label: 'Executive Summary', data: null, loading: false, error: null }
  ]
}


const getHeaders = (): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
}

const getBrandFunnelExecutiveReport = async (): Promise<any> => {
  try {
    // Use the /api/v1 endpoint for brand funnel report
    const response = await axios.get('/api/v1/executive-reports/brand-funnel', {
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Brand Funnel Executive Report not yet generated')
    }
    throw error
  }
}

const getExecutiveSummary = async (category: string, jobName?: string): Promise<any> => {
  if (!category) {
    throw new Error('Category is required')
  }
  if(category == 'default') {
    return
  }
  try {
    // Use the /api/v1 endpoint with optional job parameter
    let url = `/api/v1/executive-summaries/${encodeURIComponent(category)}`
    if (jobName) {
      url += `?job=${encodeURIComponent(jobName)}`
    }
    const response = await axios.get(url, {
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error(`Executive summary not yet generated for category: ${category}`)
    }
    throw error
  }
}

export const useExecutiveReports = () => {
  const account = useAccount()
  const router = useRouter()
  // Get job name for categories
  const jobName = account.job?.name || account.job?.displayName || undefined
  
  const [copySuccess, setCopySuccess] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)
  const [categoriesData, setCategoriesData] = useState<CategoryConfig[]>(getCategoriesForJob(jobName))
  const [dataStructure, setDataStructure] = useState<'legacy' | 'categories'>('legacy')
  
  // Report type state
  const [reportType, setReportType] = useState<ReportType>('category-reports')
  const [brandFunnelData, setBrandFunnelData] = useState<BrandFunnelReportData | null>(null)
  const [brandFunnelLoading, setBrandFunnelLoading] = useState(false)
  const [brandFunnelError, setBrandFunnelError] = useState<string | null>(null)
  
  // Approval and editing state
  const [isApproved, setIsApproved] = useState(false)
  const [editingSection, setEditingSection] = useState<{title: string, content: string} | null>(null)
  
  // Table modal state
  const [isTableModalOpen, setIsTableModalOpen] = useState(false)

  // Modal table control state
  const [modalIsFullscreen, setModalIsFullscreen] = useState(false)
  const [modalIsTextWrapped, setModalIsTextWrapped] = useState(false)
  const [modalIsSearchVisible, setModalIsSearchVisible] = useState(false)
  const [modalIsSideExpanded, setModalIsSideExpanded] = useState(false)

  const [latestRunSummary, setLatestRunSummary] = useState<ExecutiveReportRunSummaryResponse | null>(null)
  const [runProgress, setRunProgress] = useState<ExecutiveReportRunProgress | null>(null)
  const [runHistory, setRunHistory] = useState<ExecutiveReportRunRecord[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [runsError, setRunsError] = useState<string | null>(null)

  const fetchRunEndpoints = async (customerName: string, jobName: string) => {
    const basePath = `/api/v1/executive-reports/${encodeURIComponent(customerName)}/${encodeURIComponent(jobName)}`
    const summary: ExecutiveReportRunSummaryResponse | null = null
    const progress: ExecutiveReportRunProgress | null = null
    const history: ExecutiveReportRunRecord[] = []
    const error: string | null = null

    // try {
    //   const latestResponse = await axios.get<ExecutiveReportRunSummaryResponse>(`${basePath}/runs/latest`, {
    //     headers: getHeaders(),
    //   })
    //   summary = latestResponse.data
    // } catch (latestError) {
    //   if (!(axios.isAxiosError(latestError) && latestError.response?.status === 404)) {
    //     error = 'Failed to load latest executive report run'
    //     console.error('Failed to load latest run', latestError)
    //   }
    // }

    // if (summary) {
    //   try {
    //     const statusResponse = await axios.get<ExecutiveReportRunSummaryResponse>(`${basePath}/runs/${summary.run.version}/status`, {
    //       headers: getHeaders(),
    //     })
    //     progress = statusResponse.data.progress || null
    //   } catch (statusError) {
    //     if (!(axios.isAxiosError(statusError) && statusError.response?.status === 404)) {
    //       console.error('Failed to load run status', statusError)
    //     }
    //   }
    // }

    // try {
    //   const historyResponse = await axios.get<{ runs: ExecutiveReportRunRecord[] }>(`${basePath}/runs`, {
    //     headers: getHeaders(),
    //   })
    //   history = historyResponse.data?.runs || []
    // } catch (historyError) {
    //   if (!(axios.isAxiosError(historyError) && historyError.response?.status === 404)) {
    //     if (!error) {
    //       error = 'Failed to load run history'
    //     }
    //     console.error('Failed to load run history', historyError)
    //   }
    // }

    return { summary, progress, history, error }
  }

  const table = router.params.table
  const fullTable = table ? table.match(/\.cbt$/) ? table : `${table}.cbt` : ''

  // Load brand funnel report
  const loadBrandFunnelReport = useCallback(async () => {
    if (brandFunnelLoading) return

    setBrandFunnelLoading(true)
    setBrandFunnelError(null)

    try {
      const data = await getBrandFunnelExecutiveReport()
      setBrandFunnelData(data)
    } catch (error) {
      console.error('Error loading brand funnel report:', error)
      setBrandFunnelError('Brand Funnel Executive Report not available. Please ensure the report has been generated.')
    } finally {
      setBrandFunnelLoading(false)
    }
  }, [brandFunnelLoading])

  // Load table data in modal
  const loadTableInModal = useCallback(async (tableName: string) => {
    console.log('loadTableInModal', tableName)

    const node = findNode(account.toc, (node) => node.type === 'Table' && node.value1?.replace(/\.cbt$/, '') === tableName.replace(/\.cbt$/, ''))
    if(!node) return

    router.setParams({
      table: getNodeID(node).replace(/\.cbt$/, ''),
    })

    setIsTableModalOpen(true)
  }, [
    account.toc,
  ])

  // Load executive summary for a category
  const loadExecutiveSummary = useCallback(async (categoryId: string, tabIndex: number) => {
    // Update loading state for this category
    setCategoriesData(prev => prev.map((cat, index) => 
      index === tabIndex ? { ...cat, loading: true, error: null } : cat
    ))

    try {
      // Pass the job name to the API
      const jobName = account.job?.name || account.job?.displayName || ''
      const data = await getExecutiveSummary(categoryId, jobName)
      
      // Check if we need to update categories based on the data structure
      if (data && data.categories && typeof data.categories === 'object') {
        const categoryKeys = Object.keys(data.categories)
        
        // Check if we need to update the categories list (only once when first loading)
        if (categoryKeys.length > 1 && categoriesData.length <= 1) {
          // We have a new structure with multiple categories, update the categories list
          const newCategories = categoryKeys.map((catId, index) => ({
            id: catId,
            label: formatCategoryLabel(catId),
            data: index === tabIndex ? data : null, // Only set data for current tab
            loading: false,
            error: null
          }))
          
          setCategoriesData(newCategories)
          return
        }
        
        // Extract data for the specific category from the categories structure
        const categoryData = data.categories[categoryId]
        if (categoryData) {
          const transformedData: ExecutiveReportData = {
            ...categoryData,
            metadata: data.metadata,
            categories: data.categories // Keep the full categories structure
          }
          
          // Update the categories data with the loaded data
          setCategoriesData(prev => prev.map((cat, index) => 
            index === tabIndex ? { ...cat, data: transformedData, loading: false, error: null } : cat
          ))
        } else {
          // Category not found in data, show error
          setCategoriesData(prev => prev.map((cat, index) => 
            index === tabIndex ? { 
              ...cat, 
              data: null, 
              loading: false, 
              error: `Category ${categoryId} not found in executive summary data`
            } : cat
          ))
        }
      } else {
        // Legacy structure - use data as-is
        setCategoriesData(prev => prev.map((cat, index) => 
          index === tabIndex ? { ...cat, data, loading: false, error: null } : cat
        ))
      }
    } catch (error) {
      console.error('Error loading executive summary:', error)
      // Update error state for this category
      setCategoriesData(prev => prev.map((cat, index) => 
        index === tabIndex ? { 
          ...cat, 
          data: null, 
          loading: false, 
          error: error instanceof Error ? error.message : 'Failed to load executive summary'
        } : cat
      ))
    }
  }, [account.job])

  // Effect to reset data when job changes
  useEffect(() => {
    const jobName = account.job?.name || account.job?.displayName || undefined
    const customerName = account.customer?.name || undefined

    setLatestRunSummary(null)
    setRunProgress(null)
    setRunHistory([])
    setRunsError(null)

    const newCategories = getCategoriesForJob(jobName)
    setCategoriesData(newCategories)
    setBrandFunnelData(null)
    setBrandFunnelLoading(false)
    setBrandFunnelError(null)
    setSelectedTab(0)

    if (account.job && newCategories.length > 0) {
      const firstCategory = newCategories[0]
      if (firstCategory.id !== 'default') {
        loadExecutiveSummary(firstCategory.id, 0)
      }
    }

    if (!customerName || !jobName) {
      return
    }

    let cancelled = false

    const load = async () => {
      setRunsLoading(true)
      try {
        const result = await fetchRunEndpoints(customerName, jobName)
        if (!cancelled) {
          setLatestRunSummary(result.summary)
          setRunProgress(result.progress)
          setRunHistory(result.history)
          setRunsError(result.error)
        }
      } finally {
        if (!cancelled) {
          setRunsLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [account.job?.id, account.customer?.id])

  const refreshRunData = async () => {
    const jobName = account.job?.name || account.job?.displayName || undefined
    const customerName = account.customer?.name || undefined
    if (!customerName || !jobName) {
      return
    }

    setRunsLoading(true)
    try {
      const result = await fetchRunEndpoints(customerName, jobName)
      setLatestRunSummary(result.summary)
      setRunProgress(result.progress)
      setRunHistory(result.history)
      setRunsError(result.error)
    } finally {
      setRunsLoading(false)
    }
  }
  
  // Effect to load data when report type changes or initial load
  useEffect(() => {
    if (reportType === 'brand-funnel') {
      // Load Brand Funnel report if not already loaded
      if (!brandFunnelData && !brandFunnelLoading) {
        loadBrandFunnelReport()
      }
    } else {
      // Load data for selected tab only on initial load (tab changes are handled by handleTabChange)
      const selectedCategory = categoriesData[selectedTab]
      if (!selectedCategory.data && !selectedCategory.loading) {
        loadExecutiveSummary(selectedCategory.id, selectedTab)
      }
    }
  }, [reportType, selectedTab])

  // Get current report data based on report type and selected tab
  const currentCategory = reportType === 'category-reports' ? categoriesData[selectedTab] : null
  const currentReportData = reportType === 'category-reports' ? currentCategory?.data : null

  const handleCopyToClipboard = useCallback(async (content: string) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopySuccess(true)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }, [])

  const handleEditSection = useCallback((title: string, content: string) => {
    setEditingSection({ title, content })
    // For now, just log - in a real implementation you'd open an edit modal
    console.log('Editing section:', title)
  }, [])

  const copyFullSection = useCallback(() => {
    if (reportType === 'brand-funnel' && brandFunnelData) {
      // Copy all categories content from brand funnel report
      const allContent = brandFunnelData.categories.map(category => {
        const categoryContent = category.sections.map(section => 
          `## ${section.title}\n${section.content}`
        ).join('\n\n')
        return `# ${category.categoryDisplayName}\n\n${categoryContent}`
      }).join('\n\n---\n\n')
      
      const finalContent = `# ${brandFunnelData.metadata.title}\n\n${allContent}\n\n---\n\n# ${brandFunnelData.crossCategoryInsights.title}\n\n${brandFunnelData.crossCategoryInsights.content}`
      handleCopyToClipboard(finalContent)
    } else if (currentReportData && currentReportData.pillars) {
      // Copy category report content
      const pillars = currentReportData.pillars
      const fullContent = `${pillars.pillar1.summary.title}\n${pillars.pillar1.summary.content}\n\n${pillars.pillar2.summary.title}\n${pillars.pillar2.summary.content}\n\n${pillars.pillar3.summary.title}\n${pillars.pillar3.summary.content}`
      handleCopyToClipboard(fullContent)
    }
  }, [reportType, brandFunnelData, currentReportData, handleCopyToClipboard])

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    // Force reload the data for the newly selected tab
    const targetCategory = categoriesData[newValue]
    if (targetCategory && reportType === 'category-reports') {
      loadExecutiveSummary(targetCategory.id, newValue)
    }
  }, [categoriesData, reportType, loadExecutiveSummary])

  const handleReportTypeChange = useCallback((newReportType: ReportType) => {
    setReportType(newReportType)
    // Reset selected tab when switching report types
    setSelectedTab(0)
  }, [])

  // Handle modal close and reset state
  const handleModalClose = useCallback(() => {
    setIsTableModalOpen(false)
    // Reset modal control state when modal is closed
    setModalIsFullscreen(false)
    setModalIsTextWrapped(false)
    setModalIsSearchVisible(false)
    setModalIsSideExpanded(false)

    // Clear all URL parameters (table param and all table-related params)
    router.removeParams([
      'table',
      'view',
      'frequency',
      'column_percent',
      'row_percent',
      'significance',
      'vertical_coefficient',
      'horizontal_coefficient',
      'chart_type',
      'data_type',
      'selected_rows',
      'selected_columns',
      'selected_column',
      'switch_axis',
      'fullscreen',
    ])
  }, [router])

  // Handle escape key for modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTableModalOpen) {
        handleModalClose()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [isTableModalOpen, handleModalClose])

  return {
    // State
    copySuccess,
    selectedTab,
    categoriesData,
    reportType,
    brandFunnelData,
    brandFunnelLoading,
    brandFunnelError,
    isApproved,
    editingSection,
    isTableModalOpen,
    modalTableName: fullTable,
    modalIsFullscreen,
    modalIsTextWrapped,
    modalIsSearchVisible,
    modalIsSideExpanded,
    runsLoading,
    runsError,
    latestRunSummary,
    runProgress,
    runHistory,
    currentCategory,
    currentReportData,

    // Actions
    setCopySuccess,
    setIsApproved,
    handleCopyToClipboard,
    handleEditSection,
    copyFullSection,
    handleTabChange,
    handleReportTypeChange,
    loadTableInModal,
    handleModalClose,
    setModalIsFullscreen,
    setModalIsTextWrapped,
    setModalIsSearchVisible,
    setModalIsSideExpanded,
    refreshRunData,
  }
}
